/** Ensures the default theme is only defined once across all editor instances. */
let themeDefined = false;

/**
 * Checks OS-level accessibility preferences for high-contrast mode via
 * `forced-colors: active` and `prefers-contrast: more` media queries.
 */
const needsHighContrast = (): boolean => {
  const forcedColors = globalThis.matchMedia?.("(forced-colors: active)");
  const prefersMore = globalThis.matchMedia?.("(prefers-contrast: more)");
  return Boolean(forcedColors?.matches || prefersMore?.matches);
};

const themeRules: monaco.editor.IStandaloneThemeData["rules"] = [
  { token: "tag", foreground: "cba6f7" },
  { token: "keyword", foreground: "cba6f7" },
  { token: "keyword.control", foreground: "cba6f7" },
  { token: "attribute.name", foreground: "89b4fa" },
  { token: "type", foreground: "89b4fa", fontStyle: "italic" },
  { token: "string", foreground: "a6e3a1" },
  { token: "attribute.value", foreground: "a6e3a1" },
  { token: "number", foreground: "fab387" },
  { token: "constant", foreground: "fab387" },
  { token: "comment", foreground: "7f849c", fontStyle: "italic" },
  { token: "delimiter", foreground: "a6adc8" },
  { token: "identifier", foreground: "cdd6f4" },
  { token: "variable", foreground: "f5e0dc" },
  { token: "regexp", foreground: "f38ba8" },
  { token: "operator", foreground: "94e2d5" },
  { token: "predefined", foreground: "f9e2af" },
  { token: "entity", foreground: "74c7ec" },
  { token: "namespace", foreground: "b4befe" },
  // JSON-specific tokens
  { token: "string.key.json", foreground: "89b4fa" },
  { token: "string.value.json", foreground: "a6e3a1" },
  { token: "number.json", foreground: "fab387" },
  { token: "keyword.json", foreground: "fab387" },
  // GraphQL-specific tokens
  { token: "keyword.gql", foreground: "cba6f7" },
  { token: "type.identifier.gql", foreground: "89b4fa" },
  { token: "annotation.gql", foreground: "f9e2af" },
  { token: "variable.gql", foreground: "f5e0dc" },
  { token: "string.gql", foreground: "a6e3a1" },
  { token: "number.gql", foreground: "fab387" },
  { token: "comment.gql", foreground: "7f849c", fontStyle: "italic" },
  { token: "keyword.operator.gql", foreground: "94e2d5" },
  // HTTP-specific tokens
  { token: "keyword.http", foreground: "cba6f7" },
  { token: "attribute.name.http", foreground: "89b4fa" },
  { token: "string.http", foreground: "a6e3a1" },
  { token: "string.link.http", foreground: "74c7ec" },
  { token: "number.http", foreground: "fab387" },
  { token: "variable.http", foreground: "f5e0dc" },
  { token: "comment.http", foreground: "7f849c", fontStyle: "italic" },
  // PHP-specific tokens
  { token: "keyword.php", foreground: "cba6f7" },
  { token: "type.identifier.php", foreground: "89b4fa" },
  { token: "variable.php", foreground: "f5e0dc" },
  { token: "string.php", foreground: "a6e3a1" },
  { token: "string.escape.php", foreground: "f38ba8" },
  { token: "number.php", foreground: "fab387" },
  { token: "comment.php", foreground: "7f849c", fontStyle: "italic" },
  { token: "operator.php", foreground: "94e2d5" },
  { token: "annotation.php", foreground: "f9e2af" },
  // Bash-specific tokens
  { token: "keyword.bash", foreground: "cba6f7" },
  { token: "keyword.operator.bash", foreground: "94e2d5" },
  { token: "predefined.bash", foreground: "f9e2af" },
  { token: "variable.bash", foreground: "f5e0dc" },
  { token: "string.bash", foreground: "a6e3a1" },
  { token: "string.escape.bash", foreground: "f38ba8" },
  { token: "string.heredoc.bash", foreground: "a6e3a1" },
  { token: "number.bash", foreground: "fab387" },
  { token: "comment.bash", foreground: "7f849c", fontStyle: "italic" },
  { token: "operator.bash", foreground: "94e2d5" },
];

const themeColors: Record<string, string> = {
  "editor.background": "#00000000",
  "editor.foreground": "#cdd6f4",
  "editorCursor.foreground": "#f5e0dc",
  "editor.selectionBackground": "#45475a80",
  "editor.lineHighlightBackground": "#31324433",
  "editorWhitespace.foreground": "#58587044",
};

/**
 * Determines the final Monaco theme to apply. Registers the Catppuccin Mocha
 * based default theme once globally and returns its name.
 * Overrides with `hc-black` when the OS requests high contrast.
 *
 * @param monacoGlobal - The global Monaco namespace used to call `defineTheme`.
 * @param baseTheme - The theme explicitly set by the consumer (e.g. `"vs-dark"`).
 * @param autoDetectHighContrast - Whether to check OS high-contrast preferences.
 * @returns The resolved Monaco theme id to pass to `editor.create` and `setTheme`.
 */
export const resolveTheme = (
  monacoGlobal: typeof monaco,
  baseTheme: string,
  autoDetectHighContrast: boolean,
): string => {
  if (autoDetectHighContrast && needsHighContrast() && !/hc/i.test(baseTheme)) {
    return "hc-black";
  }

  if (!themeDefined) {
    monacoGlobal.editor.defineTheme("econnect-default", {
      base: "vs-dark",
      inherit: true,
      rules: themeRules,
      colors: themeColors,
    });
    themeDefined = true;
  }

  return "econnect-default";
};
